import { useState } from 'react';
import { Copy, Download, Check } from 'lucide-react';
import ToolLayout from '../../components/dev-tools/ToolLayout';
import { copyToClipboard, downloadFile } from '../../utils/clipboard';

export default function JsonFormatter() {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [indent, setIndent] = useState(2);
  const [sortKeys, setSortKeys] = useState(false);
  const [copied, setCopied] = useState(false);

  const sortObjectKeys = (obj: unknown): unknown => {
    if (Array.isArray(obj)) {
      return obj.map(sortObjectKeys);
    }
    if (obj !== null && typeof obj === 'object') {
      return Object.keys(obj as Record<string, unknown>)
        .sort()
        .reduce((result: Record<string, unknown>, key) => {
          result[key] = sortObjectKeys((obj as Record<string, unknown>)[key]);
          return result;
        }, {});
    }
    return obj;
  };

  const sanitizeJsJson = (str: string): string => {
    // Replace single quotes with double quotes
    let sanitized = str.replace(/'/g, '"');
    // Add quotes to unquoted keys (simple regex for common cases)
    sanitized = sanitized.replace(/([{,]\s*)([a-zA-Z_$][a-zA-Z0-9_$]*)\s*:/g, '$1"$2":');
    return sanitized;
  };

  const formatJson = () => {
    try {
      let jsonStr = input.trim();
      // Try to parse as-is first
      let parsed;
      try {
        parsed = JSON.parse(jsonStr);
      } catch {
        // If it fails, try sanitizing JS-like JSON
        jsonStr = sanitizeJsJson(jsonStr);
        parsed = JSON.parse(jsonStr);
      }
      const formatted = JSON.stringify(sortKeys ? sortObjectKeys(parsed) : parsed, null, indent);
      setOutput(formatted);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid JSON');
      setOutput('');
    }
  };

  const handleCopy = async () => {
    if (output) {
      const success = await copyToClipboard(output);
      if (success) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  const handleDownload = () => {
    if (output) {
      downloadFile(output, 'formatted.json', 'application/json');
    }
  };

  const minifyJson = () => {
    try {
      let jsonStr = input.trim();
      // Try to parse as-is first
      let parsed;
      try {
        parsed = JSON.parse(jsonStr);
      } catch {
        // If it fails, try sanitizing JS-like JSON
        jsonStr = sanitizeJsJson(jsonStr);
        parsed = JSON.parse(jsonStr);
      }
      const minified = JSON.stringify(parsed);
      setOutput(minified);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid JSON');
      setOutput('');
    }
  };

  return (
    <ToolLayout
      title="JSON Formatter"
      description="Format, validate, and beautify JSON with syntax highlighting"
    >
      <div className="space-y-6">
        {/* Options */}
        <div className="card">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <label htmlFor="indent" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Indent:
              </label>
              <select
                id="indent"
                value={indent}
                onChange={e => setIndent(Number(e.target.value))}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:focus:border-primary-400"
              >
                <option value={2}>2 spaces</option>
                <option value={4}>4 spaces</option>
                <option value={0}>Tab</option>
              </select>
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={sortKeys}
                onChange={e => setSortKeys(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800"
              />
              Sort keys alphabetically
            </label>

            <div className="ml-auto flex gap-2">
              <button onClick={formatJson} className="btn-primary">
                Format
              </button>
              <button onClick={minifyJson} className="btn-secondary">
                Minify
              </button>
            </div>
          </div>
        </div>

        {/* Input/Output */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Input */}
          <div className="card">
            <h3 className="mb-3 font-semibold text-gray-900 dark:text-gray-50">Input JSON</h3>
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder='{"name": "John", "age": 30}'
              className="h-96 w-full rounded-lg border border-gray-300 bg-white p-4 font-mono text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:placeholder-gray-500 dark:focus:border-primary-400"
            />
          </div>

          {/* Output */}
          <div className="card">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="mb-3 font-semibold text-gray-900 dark:text-gray-50">Formatted Output</h3>
              {output && (
                <div className="flex gap-2">
                  <button
                    onClick={handleCopy}
                    className="flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-medium text-primary-600 transition-colors hover:bg-primary-50"
                    title="Copy to clipboard"
                  >
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    onClick={handleDownload}
                    className="flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-medium text-primary-600 transition-colors hover:bg-primary-50"
                    title="Download as file"
                  >
                    <Download size={16} />
                    Download
                  </button>
                </div>
              )}
            </div>

            {error && (
              <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950 dark:text-red-300">
                <strong>Error:</strong> {error}
              </div>
            )}

            <pre className="h-96 w-full overflow-auto rounded-lg border border-gray-300 bg-gray-50 p-4 font-mono text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200">
              {output || 'Formatted JSON will appear here...'}
            </pre>
          </div>
        </div>
      </div>
    </ToolLayout>
  );
}
